import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { InvoiceApi } from '@/lib/invoices-api';
import { queryKeys } from '@/config/query-keys';
import type { ExtendedListParams } from '@/types/search-criteria';
import type { InvoicesList } from '@/types/invoice';

export const STALE_TIME = 1000 * 60 * 10;

export function useInvoices(params?: ExtendedListParams) {
  const { selectedCustomer } = useAuth();
  const customerId = selectedCustomer?.customerId;

  return useQuery({
    queryKey: queryKeys.invoices.list(customerId ?? 0, params),
    queryFn: () => InvoiceApi.getInvoices(params),
    staleTime: STALE_TIME,
    enabled: customerId != null,
  });
}

/** Every invoice of one contract (its own and its lines'), in backend order — oldest
 * period first. Shared by the contract cards and the box's invoices page, so both
 * read the same cache entry and agree on what's owed. */
export function contractInvoicesQuery(customerId: number, contractId: number) {
  return queryOptions({
    queryKey: queryKeys.invoices.byContract(customerId, contractId),
    queryFn: () => InvoiceApi.getInvoicesByContractId(contractId),
    staleTime: STALE_TIME,
  });
}

// Module-level so `select` keeps its identity and only re-runs when the data changes.
function newestFirst(list: InvoicesList): InvoicesList {
  return {
    ...list,
    items: [...list.items].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    ),
  };
}

export function useInvoicesByContract(contractId: number) {
  const { selectedCustomer } = useAuth();
  const customerId = selectedCustomer?.customerId;

  return useQuery({
    ...contractInvoicesQuery(customerId ?? 0, contractId),
    select: newestFirst,
    enabled: !!contractId && customerId != null,
  });
}

export function useInvoiceMutations() {
  const { selectedCustomer } = useAuth();
  const queryClient = useQueryClient();
  const customerId = selectedCustomer?.customerId;

  const invalidateAll = () => {
    if (customerId != null) {
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all(customerId) });
    }
  };

  return { invalidateAll };
}
